import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import { SERVER_BASE_URL } from "../Config";

interface Provider {
  id: number;
  email: string;
  name: string;
  phone: string;
  npi: number | null;
  practiceName?: string;
  stateLicenses?: string;
  mobilePhone?: string;
  fax?: string;
  salesRep?: string;
  contact?: string;
  temporary_password?: string;
}

interface CreateProviderResponse {
  success: boolean;
  message: string;
  provider?: Provider;
  email_sent?: boolean;
}

const ProviderManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create provider dialog state
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newProviderEmail, setNewProviderEmail] = useState("");
  const [newProviderName, setNewProviderName] = useState("");
  const [newProviderPhone, setNewProviderPhone] = useState("");
  const [newProviderNpi, setNewProviderNpi] = useState("");
  const [newProviderPracticeName, setNewProviderPracticeName] = useState("");
  const [newProviderStateLicenses, setNewProviderStateLicenses] = useState("");
  const [newProviderMobilePhone, setNewProviderMobilePhone] = useState("");
  const [newProviderFax, setNewProviderFax] = useState("");
  const [newProviderSalesRep, setNewProviderSalesRep] = useState("");
  const [newProviderContact, setNewProviderContact] = useState("");
  const [createdProvider, setCreatedProvider] = useState<Provider | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isReactivation, setIsReactivation] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Delete provider dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState<Provider | null>(null);

  // Edit provider dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [editProviderName, setEditProviderName] = useState("");
  const [editProviderPhone, setEditProviderPhone] = useState("");
  const [editProviderNpi, setEditProviderNpi] = useState("");
  const [editProviderPracticeName, setEditProviderPracticeName] = useState("");
  const [editProviderStateLicenses, setEditProviderStateLicenses] = useState("");
  const [editProviderMobilePhone, setEditProviderMobilePhone] = useState("");
  const [editProviderFax, setEditProviderFax] = useState("");
  const [editProviderSalesRep, setEditProviderSalesRep] = useState("");
  const [editProviderContact, setEditProviderContact] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // Tabs and deleted providers state
  const [activeTab, setActiveTab] = useState(0);
  const [deletedProviders, setDeletedProviders] = useState<Provider[]>([]);
  const [reactivatingProvider, setReactivatingProvider] = useState<Provider | null>(null);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);

  // Load providers
  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setProviders(data.providers);
      } else {
        setError(data.message || "Failed to load providers");
      }
    } catch (err) {
      setError(`Failed to load providers: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load deleted providers
  const loadDeletedProviders = async () => {
    try {
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/deleted`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setDeletedProviders(data.providers);
      }
    } catch (err) {
      setError(`Failed to load deleted providers: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Reactivate provider
  const handleReactivateProvider = async () => {
    if (!reactivatingProvider) return;
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/deleted/${reactivatingProvider.id}/reactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setOpenReactivateDialog(false);
        setReactivatingProvider(null);
        setIsPasswordReset(true);
        setIsReactivation(true);
        setEmailSent(!!data.email_sent);
        setCreatedProvider({
          ...data.provider,
          temporary_password: data.temporary_password
        });
        setOpenCreateDialog(true);
        setSuccess("Provider reactivated successfully!");
        loadProviders();
        loadDeletedProviders();
      } else {
        setError(data.message || "Failed to reactivate provider");
      }
    } catch (err) {
      setError(`Failed to reactivate provider: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
    loadDeletedProviders();
  }, []);

  // Create provider
  const handleCreateProvider = async () => {
    if (!newProviderEmail || !newProviderName) {
      setError("Email and name are required");
      return;
    }
    if (!newProviderNpi) {
      setError("NPI is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
        body: JSON.stringify({
          email: newProviderEmail,
          name: newProviderName,
          phone: newProviderPhone,
          npi: newProviderNpi ? parseInt(newProviderNpi) : null,
          practice_name: newProviderPracticeName,
          state_licenses: newProviderStateLicenses,
          mobile_phone: newProviderMobilePhone,
          fax: newProviderFax,
          sales_rep: newProviderSalesRep,
          contact: newProviderContact,
        }),
      });

      const data: CreateProviderResponse = await response.json();
      if (data.success && data.provider) {
        setIsPasswordReset(false);
        setEmailSent(!!data.email_sent);
        setCreatedProvider(data.provider);
        setSuccess(`Provider created successfully!`);
        loadProviders();
        setNewProviderEmail("");
        setNewProviderName("");
        setNewProviderPhone("");
        setNewProviderNpi("");
        setNewProviderPracticeName("");
        setNewProviderStateLicenses("");
        setNewProviderMobilePhone("");
        setNewProviderFax("");
        setNewProviderSalesRep("");
        setNewProviderContact("");
      } else {
        setError(data.message || "Failed to create provider");
      }
    } catch (err) {
      setError(`Failed to create provider: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Reset and view provider password
  const handleViewPassword = async (providerId: number, providerName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Reset password for ${providerName}? This will generate a new temporary password.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/${providerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Show password in a provider object format for the dialog
        const providerWithPassword = providers.find(p => p.id === providerId);
        if (providerWithPassword) {
          setIsPasswordReset(true);
          setEmailSent(!!data.email_sent);
          setCreatedProvider({
            ...providerWithPassword,
            temporary_password: data.temporary_password
          });
          setOpenCreateDialog(true);
        }
        setSuccess("Password reset successfully");
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (err) {
      setError(`Failed to reset password: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = (password: string) => {
    navigator.clipboard.writeText(password);
    setSuccess("Password copied to clipboard!");
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setCreatedProvider(null);
    setIsPasswordReset(false);
    setIsReactivation(false);
    setEmailSent(false);
  };

  // Open edit dialog
  const handleOpenEditDialog = (provider: Provider) => {
    setEditingProvider(provider);
    setEditProviderName(provider.name);
    setEditProviderPhone(provider.phone || "");
    setEditProviderNpi(provider.npi ? provider.npi.toString() : "");
    setEditProviderPracticeName(provider.practiceName || "");
    setEditProviderStateLicenses(provider.stateLicenses || "");
    setEditProviderMobilePhone(provider.mobilePhone || "");
    setEditProviderFax(provider.fax || "");
    setEditProviderSalesRep(provider.salesRep || "");
    setEditProviderContact(provider.contact || "");
    setOpenEditDialog(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingProvider(null);
    setEditProviderName("");
    setEditProviderPhone("");
    setEditProviderNpi("");
    setEditProviderPracticeName("");
    setEditProviderStateLicenses("");
    setEditProviderMobilePhone("");
    setEditProviderFax("");
    setEditProviderSalesRep("");
    setEditProviderContact("");
    setSelectedFile(null);
    setUploadStatus(null);
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setUploadStatus(null); // Clear previous upload status
    }
  };

  // Handle CSV upload
  const handleUploadCSV = async () => {
    if (!selectedFile || !editingProvider) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/${editingProvider.id}/upload_patients`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${pharmacistToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setUploadStatus({ type: 'success', message: data.message });
        setSuccess(data.message);
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setUploadStatus({ type: 'error', message: data.message || "Failed to upload CSV" });
        setError(data.message || "Failed to upload CSV");
      }
    } catch (err) {
      const errorMsg = `Failed to upload CSV: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setUploadStatus({ type: 'error', message: errorMsg });
      setError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Delete provider
  const handleDeleteProvider = async () => {
    if (!deletingProvider) return;
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/${deletingProvider.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`${deletingProvider.name} has been deactivated.`);
        setOpenDeleteDialog(false);
        setDeletingProvider(null);
        loadProviders();
        loadDeletedProviders();
      } else {
        setError(data.message || "Failed to delete provider");
      }
    } catch (err) {
      setError(`Failed to delete provider: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Save edited provider
  const handleSaveProvider = async () => {
    if (!editingProvider || !editProviderName) {
      setError("Name is required");
      return;
    }
    if (!editProviderNpi) {
      setError("NPI is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/providers/${editingProvider.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
        body: JSON.stringify({
          name: editProviderName,
          phone: editProviderPhone,
          npi: editProviderNpi ? parseInt(editProviderNpi) : null,
          practice_name: editProviderPracticeName,
          state_licenses: editProviderStateLicenses,
          mobile_phone: editProviderMobilePhone,
          fax: editProviderFax,
          sales_rep: editProviderSalesRep,
          contact: editProviderContact,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess("Provider updated successfully!");
        loadProviders();
        handleCloseEditDialog();
      } else {
        setError(data.message || "Failed to update provider");
      }
    } catch (err) {
      setError(`Failed to update provider: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 0, sm: 3 } }} elevation={isMobile ? 0 : 1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant={isMobile ? "h6" : "h5"}>Provider Accounts</Typography>
          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={isMobile ? undefined : <PersonAddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
              sx={{ whiteSpace: "nowrap", ml: 1, fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
            >
              {isMobile ? "Create" : "Create Provider"}
            </Button>
          )}
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Active" />
          <Tab label="Deactivated" />
        </Tabs>

        {activeTab === 1 ? (
          isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {deletedProviders.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                  No deactivated providers
                </Typography>
              )}
              {deletedProviders.map((provider) => (
                <Card key={provider.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                          {provider.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                          {provider.email}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RestoreIcon />}
                        onClick={() => { setReactivatingProvider(provider); setOpenReactivateDialog(true); }}
                        disabled={loading}
                        sx={{ ml: 1, whiteSpace: "nowrap" }}
                      >
                        Reactivate
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>NPI</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedProviders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          No deactivated providers
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {deletedProviders.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>{provider.name}</TableCell>
                      <TableCell>{provider.email}</TableCell>
                      <TableCell>{provider.phone || "—"}</TableCell>
                      <TableCell>{provider.npi || "—"}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          startIcon={<RestoreIcon />}
                          onClick={() => { setReactivatingProvider(provider); setOpenReactivateDialog(true); }}
                          disabled={loading}
                        >
                          Reactivate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {providers.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No providers found
              </Typography>
            )}
            {providers.map((provider) => (
              <Card key={provider.id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {provider.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                        {provider.email}
                      </Typography>
                      {provider.phone && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {provider.phone}
                        </Typography>
                      )}
                      {provider.npi && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          NPI: {provider.npi}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", ml: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(provider)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleViewPassword(provider.id, provider.name)}
                        disabled={loading}
                      >
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setDeletingProvider(provider); setOpenDeleteDialog(true); }}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>NPI</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No providers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {providers.map((provider) => (
                  <TableRow key={provider.id}>
                    <TableCell>{provider.name}</TableCell>
                    <TableCell>{provider.email}</TableCell>
                    <TableCell>{provider.phone || "—"}</TableCell>
                    <TableCell>{provider.npi || "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(provider)}
                        disabled={loading}
                        title="Edit Provider"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleViewPassword(provider.id, provider.name)}
                        disabled={loading}
                        title="Reset Password"
                      >
                        <LockResetIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => { setDeletingProvider(provider); setOpenDeleteDialog(true); }}
                        disabled={loading}
                        title="Deactivate Provider"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Provider Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>
          {createdProvider ? "Provider Password" : "Create New Provider"}
        </DialogTitle>
        <DialogContent>
          {createdProvider ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {createdProvider.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {createdProvider.email}
              </Typography>
              {createdProvider.phone && (
                <Typography variant="body1" gutterBottom>
                  <strong>Phone:</strong> {createdProvider.phone}
                </Typography>
              )}
              {emailSent ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                  {isReactivation ? "Reactivation" : isPasswordReset ? "Password reset" : "Welcome"} email sent to {createdProvider.email}
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Password ready!
                  </Alert>
                  <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Temporary Password:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={createdProvider.temporary_password}
                        sx={{ fontFamily: "monospace", fontSize: "1rem" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPassword(createdProvider.temporary_password!)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
                      Make sure to copy this password - it won't be shown again!
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    sx={{ mt: 2 }}
                    fullWidth
                    onClick={() => {
                      const subject = isReactivation
                        ? "Your Chemist Shop Account Has Been Reactivated"
                        : isPasswordReset ? "Your Chemist Shop Password Has Been Reset" : "Your Chemist Shop Account";
                      const body = isReactivation
                        ? `Hi ${createdProvider.name},\n\nYour account on The Chemist Shop has been reactivated.\n\nEmail: ${createdProvider.email}\nTemporary Password: ${createdProvider.temporary_password}\n\nPlease click the link below to log in and change your password:\nhttps://providers.thechemistshoppharmacy.com/login\n\nThank you,\nThe Chemist Shop`
                        : isPasswordReset
                        ? `Hi ${createdProvider.name},\n\nYour password for The Chemist Shop has been reset.\n\nEmail: ${createdProvider.email}\nTemporary Password: ${createdProvider.temporary_password}\n\nPlease click the link below to log in and change your password:\nhttps://providers.thechemistshoppharmacy.com/login\n\nThank you,\nThe Chemist Shop`
                        : `Hi ${createdProvider.name},\n\nYour account on The Chemist Shop has been created.\n\nEmail: ${createdProvider.email}\nTemporary Password: ${createdProvider.temporary_password}\n\nPlease click the link below to log in and change your password:\nhttps://providers.thechemistshoppharmacy.com/login\n\nThank you,\nThe Chemist Shop`;
                      window.location.href = `mailto:${encodeURIComponent(createdProvider.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                  >
                    {isReactivation ? "Send Reactivation Email" : isPasswordReset ? "Send Password Reset Email" : "Send Welcome Email"}
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={newProviderEmail}
                onChange={(e) => setNewProviderEmail(e.target.value)}
                required
              />
              <TextField
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={newProviderName}
                onChange={(e) => setNewProviderName(e.target.value)}
                required
              />
              <TextField
                margin="dense"
                label="Office Phone"
                type="tel"
                fullWidth
                value={newProviderPhone}
                onChange={(e) => setNewProviderPhone(e.target.value)}
              />
              <TextField
                margin="dense"
                label="NPI"
                type="number"
                fullWidth
                required
                value={newProviderNpi}
                onChange={(e) => setNewProviderNpi(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Clinic Name"
                type="text"
                fullWidth
                value={newProviderPracticeName}
                onChange={(e) => setNewProviderPracticeName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="State Licenses"
                type="text"
                fullWidth
                value={newProviderStateLicenses}
                onChange={(e) => setNewProviderStateLicenses(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Mobile Phone"
                type="tel"
                fullWidth
                value={newProviderMobilePhone}
                onChange={(e) => setNewProviderMobilePhone(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Fax"
                type="tel"
                fullWidth
                value={newProviderFax}
                onChange={(e) => setNewProviderFax(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Sales Rep"
                type="text"
                fullWidth
                value={newProviderSalesRep}
                onChange={(e) => setNewProviderSalesRep(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Contact"
                type="text"
                fullWidth
                value={newProviderContact}
                onChange={(e) => setNewProviderContact(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                A random 10-character password will be generated automatically.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>
            {createdProvider ? "Close" : "Cancel"}
          </Button>
          {!createdProvider && (
            <Button
              onClick={handleCreateProvider}
              variant="contained"
              disabled={loading || !newProviderEmail || !newProviderName}
            >
              Create Provider
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Provider Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setDeletingProvider(null); }} maxWidth="xs" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>Deactivate Provider</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{deletingProvider?.name}</strong>? They will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDeleteDialog(false); setDeletingProvider(null); }}>Cancel</Button>
          <Button onClick={handleDeleteProvider} variant="contained" color="error" disabled={loading}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate Provider Confirmation Dialog */}
      <Dialog open={openReactivateDialog} onClose={() => { setOpenReactivateDialog(false); setReactivatingProvider(null); }} maxWidth="xs" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>Reactivate Provider</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reactivate <strong>{reactivatingProvider?.name}</strong>? This will reset their password and restore access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenReactivateDialog(false); setReactivatingProvider(null); }}>Cancel</Button>
          <Button onClick={handleReactivateProvider} variant="contained" color="primary" disabled={loading}>
            Reactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Provider Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>Edit Provider</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            {editingProvider && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Email:</strong> {editingProvider.email}
              </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              type="text"
              fullWidth
              value={editProviderName}
              onChange={(e) => setEditProviderName(e.target.value)}
              required
            />
            <TextField
              margin="dense"
              label="NPI"
              type="number"
              fullWidth
              required
              value={editProviderNpi}
              onChange={(e) => setEditProviderNpi(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Clinic Name"
              type="text"
              fullWidth
              value={editProviderPracticeName}
              onChange={(e) => setEditProviderPracticeName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="State Licenses"
              type="text"
              fullWidth
              value={editProviderStateLicenses}
              onChange={(e) => setEditProviderStateLicenses(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Office Phone"
              type="tel"
              fullWidth
              value={editProviderPhone}
              onChange={(e) => setEditProviderPhone(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Mobile Phone"
              type="tel"
              fullWidth
              value={editProviderMobilePhone}
              onChange={(e) => setEditProviderMobilePhone(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Fax"
              type="tel"
              fullWidth
              value={editProviderFax}
              onChange={(e) => setEditProviderFax(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Sales Rep"
              type="text"
              fullWidth
              value={editProviderSalesRep}
              onChange={(e) => setEditProviderSalesRep(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Contact"
              type="text"
              fullWidth
              value={editProviderContact}
              onChange={(e) => setEditProviderContact(e.target.value)}
            />

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" gutterBottom>
                Upload Patients CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload a CSV file with patient data (PATLNAME, PATFNAME, PATDOB, PATPHONE, STREET, CITY, STATE, ZIP)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                href="/patient_upload_template.csv"
                download="patient_upload_template.csv"
                sx={{ mb: 1 }}
              >
                Download Template
              </Button>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploading}
                >
                  Choose File
                  <input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {selectedFile.name}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleUploadCSV}
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                  </>
                )}
              </Box>
              {uploadStatus && (
                <Alert
                  severity={uploadStatus.type}
                  onClose={() => setUploadStatus(null)}
                  sx={{ mt: 2 }}
                >
                  {uploadStatus.message}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveProvider}
            variant="contained"
            disabled={loading || !editProviderName}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderManagement;
